<template>
  <LoaderFullPage v-if="store.loadingPetitions" />
  <template v-else-if="store.petitions.length > 0">
    <div class="englobe-params">
      <div class="params-row">
        <div>Total: {{ store.petitionsCount }}</div>
        <div class="page-settings">
          <DropdownMenu
            v-model="selectedSorterOption"
            :options="sortersOptions"
            label="Trier par :"
          />

          <DropdownMenu
            v-model="selectedLimitOption"
            :options="limitOptions"
            label="Pétitions par page :"
          />
        </div>
      </div>
    </div>
    <ul class="petitions-list">
      <li v-for="petition in store.petitions" :key="petition._id">
        <div class="card">
          <div class="header">
            <div>{{ getDateShortFormat(petition.published_at) }}</div>
            <div class="signatures">
              <i class="fa-regular fa-pen-field" aria-label="Signatures"></i>
              {{ formatNumbers(petition.signatures_number) }} /
              {{ formatNumbers(petition.signatures_target) }}
            </div>
          </div>
          <div class="body">
            <div class="title">{{ petition.title }}</div>
            <div class="link">
              <RouterLink :to="`/petitions/${petition._id}`"
                >Voir la pétition</RouterLink
              >
            </div>
          </div>
        </div>
      </li>
    </ul>

    <PaginationElement
      v-if="store.petitionsCount"
      :key="`${store.petitionsCount}${store.petitionsFilters.from}${store.petitionsFilters.size}`"
      :total-items="store.petitionsCount"
      :current-page="store.petitionsFilters.from"
      :items-limit="store.petitionsFilters.size"
      @change-current-page="changeCurrentPage"
    />
  </template>
  <div v-else class="empty">
    <img
      src="@/assets/img/institutional-groups/empty-states/commissions-petitions-nodatas.svg"
      alt="aria-hidden=true"
    />
    <p>Aucune pétition associée à cet organe.</p>
  </div>
</template>

<script setup>
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import PaginationElement from "@follaw-fr/components/reusable/atoms/PaginationElement.vue";
import LoaderFullPage from "@follaw-fr/components/reusable/molecules/LoaderFullPage.vue";
import DropdownMenu from "@follaw-fr/components/reusable/atoms/DropdownMenu.vue";
import { useDropdown } from "@follaw-fr/composables/useDropdown";
import { useInstituGroupsStore } from "@follaw-fr/stores/institutional-groups";
import { getDateShortFormat } from "@follaw-fr/utils/formatDate";
import formatNumbers from "@follaw-fr/utils/formatNumbers";

const route = useRoute();
const store = useInstituGroupsStore();
const { petitionsFilters } = storeToRefs(store);

const fetchPetitions = async () => {
  petitionsFilters.value.from = 0;
  await store.fetchPetitions(route.params.missionId || route.params._id);
};

const { options: sortersOptions, selectedValue: selectedSorterOption } =
  useDropdown({
    options: {
      published_at: "Date de publication",
      signatures_number: "Nombre de signatures",
    },
    selectedFilters: petitionsFilters,
    filterKey: "sort_field", // Matches a key in petitionsFilters
    onChange: fetchPetitions,
  });

const { options: limitOptions, selectedValue: selectedLimitOption } =
  useDropdown({
    options: { 10: "10", 20: "20", 50: "50", 200: "200" },
    selectedFilters: petitionsFilters,
    filterKey: "size",
    onChange: () => {
      fetchPetitions();
    },
  });

const changeCurrentPage = (from) => {
  petitionsFilters.value.from = from;
  fetchPetitions();
};

fetchPetitions();
</script>

<style scoped lang="scss">
.empty > img {
  max-width: 247px;
}
.empty {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 50px auto;
  font-size: 1.125em;
  flex-direction: column;
  p {
    max-width: 31.3125em;
    text-align: center;
    font-weight: 700;
    font-size: 1.125em;
    color: var(--clr-n8);
  }
  small {
    font-size: 1em;
  }
}

.petitions-list {
  list-style: none;
  padding: 0;
  margin: 0;

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 10px;

  :deep(.card) {
    display: flex;
    flex-direction: column;
    height: 100%;

    .signatures {
      font-weight: 300;
    }

    .body {
      flex-grow: 1;

      display: flex;
      flex-direction: column;
      gap: 15px;

      .link {
        margin-top: auto;
        align-self: flex-end;
      }
    }
  }
}
</style>
